/** Project list: create, open, duplicate, delete (owner) or leave (shared with you). */

import { Copy, FileArchive, LibraryBig, LogOut, Mic, Plus, Trash2, UserRound, Users } from "lucide-react";
import { useEffect, useState } from "react";
import { api, fileUrl, type ProjectSummary, type User } from "./api";
import { PackageImport } from "./PackageImport";

export function Projects({ user, onOpen, onLogout }: { user: User; onOpen: (id: string) => void; onLogout: () => void }) {
  const [list, setList] = useState<ProjectSummary[] | null>(null);
  const [name, setName] = useState("");
  const [err, setErr] = useState("");
  const [importing, setImporting] = useState(false);
  const load = () => api.projects().then(setList).catch((e) => setErr(e.message));
  useEffect(() => void load(), []);

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const p = await api.create(name || "Untitled");
      onOpen(p.id);
    } catch (x) {
      setErr((x as Error).message);
    }
  };
  const act = async (fn: () => Promise<unknown>) => {
    try {
      await fn();
      await load();
    } catch (x) {
      setErr((x as Error).message);
    }
  };

  return (
    <div className="projects">
      <header>
        <h1>Whiteboard Studio</h1>
        <div className="header-actions">
          <a className="button" href="#/library" title="Images, audio and fonts shared by everyone"><LibraryBig size={15} /> Library</a>
          <a className="button" href="#/voiceover" title="Make a voiceover from a text script"><Mic size={15} /> Voiceover</a>
          <a className="button" href="#/account" title="Your account"><UserRound size={15} /> {user.name}</a>
          <button className="icon" title="Sign out" onClick={onLogout}><LogOut size={18} /></button>
        </div>
      </header>
      <form className="new-project" onSubmit={create}>
        <input placeholder="New project name" value={name} onChange={(e) => setName(e.target.value)} />
        <button className="primary"><Plus size={16} /> Create</button>
        <button type="button" onClick={() => setImporting(true)} title="Create a project from a Studio package (.zip)"><FileArchive size={15} /> Import package</button>
      </form>
      {importing && <PackageImport onClose={() => { setImporting(false); void load(); }} onOpen={onOpen} />}
      {err && <p className="bad">{err}</p>}
      {list === null ? (
        <p className="muted">Loading…</p>
      ) : list.length === 0 ? (
        <p className="muted">No projects yet.</p>
      ) : (
        <div className="cards">
          {list.map((p) => (
            <div key={p.id} className="card" onClick={() => onOpen(p.id)}>
              <div className="card-thumb">{p.thumb && <img src={fileUrl(p.id, p.thumb)} alt="" />}</div>
              <div className="card-body">
                <strong>{p.name}</strong>
                {p.role === "editor" && <span className="badge small" title="Someone shared this project with you"><Users size={11} /> Shared with you</span>}
                <span className="muted small">
                  {p.scenes} scene{p.scenes === 1 ? "" : "s"} · {p.elements} element{p.elements === 1 ? "" : "s"} · {new Date(p.updated * 1000).toLocaleString()}
                </span>
              </div>
              <div className="card-actions" onClick={(e) => e.stopPropagation()}>
                <button className="icon" title="Duplicate" onClick={() => act(() => api.duplicate(p.id))}><Copy size={15} /></button>
                {p.role === "owner" ? (
                  <button className="icon" title="Delete (moves it to the trash folder)"
                    onClick={() => confirm(`Delete "${p.name}"? It moves to the server's trash folder.`) && act(() => api.remove(p.id))}>
                    <Trash2 size={15} />
                  </button>
                ) : (
                  <button className="icon" title="Leave (remove it from your list)"
                    onClick={() => confirm(`Leave "${p.name}"? The owner can share it with you again.`) && act(() => api.removeMember(p.id, user.id))}>
                    <LogOut size={15} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
